import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Content, OivCoreSharedFacade } from '@mobi/oiv-viewer-utils-ng-jslib';
import { OIV_TYPE } from '@mobi/oiv-viewer-xml-parser-ng-jslib';
import { LoadingState } from '@mobi/rwc-uxframework-ng';

import { Index } from '../../../../share/model/atomic-model';
import { deepClone } from '../../../../util/deep-clone';
import { filterDefined } from '../../../../util/filter-defined';
import { CoverSheet } from '../../../navigation/model/cover-sheet';
import { oivViewerActions, oivViewerApprovalActions } from '../store/oiv-viewer.actions';
import { fromOivViewer } from '../store/oiv-viewer.selectors';

@Injectable({ providedIn: 'root' })
export class OivViewerFacade {
    #store = inject(Store) as Store<any>;
    #isApprovalMode = inject(OivCoreSharedFacade).isApprovalMode;

    get avbMetadata() {
        return this.#store.selectSignal(fromOivViewer.selectAvbMetaData);
    }

    get avbChapters(): Signal<Content[]> {
        return this.#store.selectSignal(fromOivViewer.selectChapters);
    }

    get urlName() {
        return this.#store.selectSignal(fromOivViewer.selectUrlName);
    }

    get coverSheet(): Signal<CoverSheet> {
        return toSignal(
            this.#store.select(fromOivViewer.selectAvbCoverSheet).pipe(
                filterDefined,
                map((content: Content) => this.#buildCoverSheet(content)),
                deepClone,
            ),
            { initialValue: {} as CoverSheet },
        );
    }

    load(urlName: string): void {
        if (this.#isApprovalMode()) {
            this.#store.dispatch(oivViewerApprovalActions.load({ urlName }));
        } else {
            this.#store.dispatch(oivViewerActions.load({ urlName }));
        }
    }

    getChapter(chapterId: string): Observable<Content | undefined> {
        return this.#store.select(fromOivViewer.selectChapterById(chapterId)).pipe(deepClone);
    }

    getSelectedChapter(chapterId: string) {
        return this.#store.selectSignal(fromOivViewer.selectChapterById(chapterId));
    }

    getIndex(): Observable<Index> {
        return this.#store.select(fromOivViewer.selectIndex).pipe(filterDefined, deepClone);
    }

    getLoadingIndex(): Observable<LoadingState> {
        return this.#store.select(fromOivViewer.selectLoadingIndex).pipe(filterDefined, deepClone);
    }

    getTheFeature(feature: string) {
        const features = this.#store.selectSignal(fromOivViewer.selectFeatures);
        return computed(() => features().includes(feature));
    }

    #buildCoverSheet(chapter: Content): CoverSheet {
        if (chapter.type === OIV_TYPE.CHAPTER) {
            const coverSheet: CoverSheet = {
                title: '',
                info: '',
                content: '',
            };
            (chapter.content as Content[]).forEach((content: Content) => {
                if (content.type === OIV_TYPE.CHAPTER_TITLE) {
                    coverSheet.title = content.content as string;
                }
                if (
                    content.type === OIV_TYPE.CONTAINER &&
                    content.content instanceof Array &&
                    content.content.length > 0
                ) {
                    (content.content as Content[]).forEach((item: Content) => {
                        if (item.type === OIV_TYPE.SECTION_TITLE) {
                            coverSheet.content = item.content as string;
                        } else if (item.type === OIV_TYPE.INFO) {
                            coverSheet.info = item.content as string;
                        }
                    });
                }
            });
            return coverSheet;
        }
        return {} as CoverSheet;
    }
}
